import { z } from "zod";
import { Architecture } from "./catalogue.js";
import { Operation } from "./operations.js";
import { Labels } from "./ssh-keys.js";

/*
 * Snapshots and backups: copies of a server's disk the project keeps
 * (resource kind `image`). A snapshot is taken by hand and stays until it is
 * deleted; a backup is made daily while backups are on, belongs to its
 * server and goes with it. Both can restore a server or start a new one.
 */

export const SavedImageKind = z.enum(["snapshot", "backup"]);
export type SavedImageKind = z.infer<typeof SavedImageKind>;

export const SavedImageStatus = z.enum(["creating", "available", "unavailable"]);
export type SavedImageStatus = z.infer<typeof SavedImageStatus>;

export const ImageDescription = z.string().trim().max(255);

/** A server an image refers to: our id while the server exists, its name as the provider kept it. */
export const ImageServer = z.object({ id: z.uuid().nullable(), name: z.string() });
export type ImageServer = z.infer<typeof ImageServer>;

export const SavedImage = z.object({
  id: z.uuid(),
  kind: SavedImageKind,
  status: SavedImageStatus,
  description: z.string(),
  /** Stored (compressed) size the provider bills snapshots by; null while it is being created. */
  sizeGb: z.number().nonnegative().nullable(),
  /** Disk a server needs to take this image; 0 while it is being created. */
  diskGb: z.number().nonnegative(),
  architecture: Architecture,
  osFlavor: z.string(),
  osVersion: z.string().nullable(),
  /** The server it was taken from (filled in by the API); null when the provider does not say. */
  createdFrom: ImageServer.nullable(),
  /** The provider's id of that server, as stored in the resource copy. */
  createdFromRef: z.string().nullable(),
  /** Backups: the server they belong to and are deleted with (filled in by the API). */
  server: ImageServer.nullable(),
  boundToRef: z.string().nullable(),
  protection: z.object({ delete: z.boolean() }),
  labels: Labels,
  createdAt: z.iso.datetime(),
});
export type SavedImage = z.infer<typeof SavedImage>;

export const ImagesQuery = z.object({
  kind: SavedImageKind.optional(),
  /** Images taken from or belonging to this server (our id). */
  serverId: z.uuid().optional(),
});
export type ImagesQuery = z.infer<typeof ImagesQuery>;

export const UpdateImageRequest = z
  .object({ description: ImageDescription.optional(), labels: Labels.optional() })
  .refine((v) => v.description !== undefined || v.labels !== undefined, "Nothing to change");
export type UpdateImageRequest = z.infer<typeof UpdateImageRequest>;

export const ImageProtectionRequest = z.object({ delete: z.boolean() });
export type ImageProtectionRequest = z.infer<typeof ImageProtectionRequest>;

/** Answer of writes the provider finishes asynchronously. */
export const ImageChange = z.object({ image: SavedImage, operation: Operation });
export type ImageChange = z.infer<typeof ImageChange>;

/** Monthly price of a snapshot: its stored size times the price per GB-month; null while unknown. */
export function snapshotMonthly(sizeGb: number | null, perGbMonth: string): number | null {
  return sizeGb === null ? null : sizeGb * Number(perGbMonth);
}
